import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { txtdb, auth } from "../../firebase-config";
import { signOut } from "firebase/auth";
import { NavLink, useNavigate } from "react-router-dom";
import { PiWalletFill, PiCheckCircleFill, PiXCircleFill, PiClipboardTextFill } from "react-icons/pi";
import { ImSpinner8 } from "react-icons/im";
import { IoIosLogOut } from "react-icons/io";
import { AiOutlineMenu } from "react-icons/ai";
import logo from "../../stock/nexus/logo.png"; 

const supportedCoins = [
  { id: "BTC", name: "Bitcoin", network: "BTC Network" },
  { id: "ETH", name: "Ethereum", network: "ERC-20" },
  { id: "USDT", name: "Tether (USDT)", network: "ERC-20" },
  { id: "SOL", name: "Solana", network: "SOL Network" }
];

function AdminWallets() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [savingCoin, setSavingCoin] = useState(null); 
  const [successStates, setSuccessStates] = useState({}); 

  // Database values
  const [wallets, setWallets] = useState({ BTC: "", ETH: "", USDT: "", SOL: "" });
  // Input box values (Starts empty!)
  const [inputs, setInputs] = useState({ BTC: "", ETH: "", USDT: "", SOL: "" });

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      const docRef = doc(txtdb, "adminSettings", "walletAddresses");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setWallets(docSnap.data());
      }
    } catch (error) {
      console.error("Error fetching wallets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to shorten the displayed current address
  const formatAddress = (addr) => {
    if (!addr) return "Not set";
    if (addr.length < 15) return addr;
    return addr.slice(0, 6) + "..." + addr.slice(-6);
  };

  // Input Handlers
  const handleChange = (e, coinId) => {
    setInputs({ ...inputs, [coinId]: e.target.value });
  };

  const handleClear = (coinId) => {
    setInputs({ ...inputs, [coinId]: "" });
  };

  const handlePaste = async (coinId) => {
    try {
      const text = await navigator.clipboard.readText();
      setInputs({ ...inputs, [coinId]: text.trim() });
    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert("Please allow clipboard permissions in your browser to use this feature.");
    }
  };

  const handleSaveSingleWallet = async (coinId) => {
    if (!inputs[coinId]) return; 
    
    setSavingCoin(coinId);
    setSuccessStates({ ...successStates, [coinId]: false });

    try {
      const docRef = doc(txtdb, "adminSettings", "walletAddresses");
      await setDoc(docRef, { [coinId]: inputs[coinId] }, { merge: true });
      
      // Update the displayed current wallet and clear the input box
      setWallets({ ...wallets, [coinId]: inputs[coinId] });
      setInputs({ ...inputs, [coinId]: "" });

      setSuccessStates({ ...successStates, [coinId]: true });
      setTimeout(() => {
        setSuccessStates((prev) => ({ ...prev, [coinId]: false }));
      }, 3000);

    } catch (error) {
      console.error(`Error updating ${coinId} wallet:`, error);
      alert(`Failed to update ${coinId} address.`);
    } finally {
      setSavingCoin(null);
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout error:", error));
  };

  return (
    <div className='layout admin'>
      <div className="content">
        
        {/* Top Navigation */}
        <div className="transactions-container container">
          <div className="navigation-container">
            <NavLink className="mobile">
              <img src={logo} alt="nexus" />
              <p className='name'>Wallet Mgmt</p>
            </NavLink>
            <p className='desktop'>Platform Wallet Management</p>
            
            <div className="actions desktop">
              <div className="quick" onClick={() => setOpen(!open)}>
                Admin Pages <AiOutlineMenu />
              </div>
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
            </div>

            <div className="actions mobile">
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
              <div className="nav" onClick={() => setOpen(!open)}>
                <AiOutlineMenu className='icon' />
              </div>
            </div>
          </div>

          {open && (
            <div className="admin-nav">
              <NavLink to="/pendingDeposits">Pending Deposits</NavLink>
              <NavLink to="/all-users">All Users</NavLink>
              <NavLink to="/copytrades">Active Copy Trades</NavLink>
              <NavLink to="/adminKycReview">KYC Reviews</NavLink>
              <NavLink to="/admin-wallets">Wallet Management</NavLink>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="pages-content" onClick={() => setOpen(false)}>
          <div className="admin-wallets-page">
            
            <div className="header-bar">
              <h1><PiWalletFill /> Receiving Addresses</h1>
              <p>Update your platform's deposit addresses individually.</p>
            </div>

            {loading ? (
              <div className="loader"><ImSpinner8 className="spin" /></div>
            ) : (
              <div className="wallets-grid">
                {supportedCoins.map((coin) => (
                  <div key={coin.id} className="wallet-card">
                    
                    <div className="card-header">
                      <div className="title-block">
                        <h3>{coin.name}</h3>
                        <p className="current-address">Active: <span>{formatAddress(wallets[coin.id])}</span></p>
                      </div>
                      <span className="network-badge">{coin.network}</span>
                    </div>

                    <div className="input-wrapper">
                      <input 
                        type="text" 
                        value={inputs[coin.id] || ""} 
                        onChange={(e) => handleChange(e, coin.id)} 
                        placeholder={`Paste new ${coin.id} address...`}
                      />
                      
                      <div className="input-actions">
                        {inputs[coin.id] && (
                          <button 
                            className="clear-btn" 
                            onClick={() => handleClear(coin.id)}
                            title="Clear input"
                          >
                            <PiXCircleFill />
                          </button>
                        )}
                        <button 
                          className="paste-btn" 
                          onClick={() => handlePaste(coin.id)}
                          title="Paste from clipboard"
                        >
                          <PiClipboardTextFill />
                        </button>
                      </div>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="update-btn" 
                        onClick={() => handleSaveSingleWallet(coin.id)}
                        disabled={savingCoin === coin.id || !inputs[coin.id]}
                      >
                        {savingCoin === coin.id ? <ImSpinner8 className="spin" /> : "Update Address"}
                      </button>
                      
                      {successStates[coin.id] && (
                        <span className="success-icon" title="Updated successfully!">
                          <PiCheckCircleFill />
                        </span>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            )}
            
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminWallets;